// Legal boilerplate generators (privacy policy, disclaimer, terms). These build
// a practical starting template from a few inputs, entirely in the browser. They
// are a convenience starting point, not legal advice.

export type LegalVariant =
  | 'privacy-policy-generator'
  | 'disclaimer-generator'
  | 'terms-and-condition-generator';

export type LegalField = { key: string; label: string; placeholder: string; default: string };

export type LegalMeta = {
  badge: string;
  title: string;
  fields: LegalField[];
};

const sharedFields: LegalField[] = [
  { key: 'company', label: 'Company or site name', placeholder: 'Acme Inc.', default: 'Acme Inc.' },
  {
    key: 'website',
    label: 'Website URL',
    placeholder: 'https://example.com',
    default: 'https://example.com',
  },
  {
    key: 'email',
    label: 'Contact email',
    placeholder: 'hello@example.com',
    default: 'hello@example.com',
  },
  {
    key: 'country',
    label: 'Governing country',
    placeholder: 'United States',
    default: 'United States',
  },
];

export const legalMeta: Record<LegalVariant, LegalMeta> = {
  'privacy-policy-generator': {
    badge: 'Privacy',
    title: 'Generate a privacy policy',
    fields: sharedFields,
  },
  'disclaimer-generator': {
    badge: 'Disclaimer',
    title: 'Generate a disclaimer',
    fields: sharedFields,
  },
  'terms-and-condition-generator': {
    badge: 'Terms',
    title: 'Generate terms and conditions',
    fields: sharedFields,
  },
};

function clean(values: Record<string, string>): {
  company: string;
  website: string;
  email: string;
  country: string;
} {
  return {
    company: values.company?.trim() || 'the Company',
    website: values.website?.trim() || 'this website',
    email: values.email?.trim() || 'the contact email',
    country: values.country?.trim() || 'the applicable country',
  };
}

function privacyPolicy(v: Record<string, string>): string {
  const { company, website, email, country } = clean(v);
  return [
    `Privacy Policy`,
    ``,
    `This Privacy Policy explains how ${company} ("we", "us", or "our") collects, uses, and protects your information when you use ${website}.`,
    ``,
    `1. Information We Collect`,
    `We may collect information you provide directly, such as your name and email address, and information collected automatically, such as your IP address, browser type, and pages visited.`,
    ``,
    `2. How We Use Information`,
    `We use the information to operate and improve our services, respond to your requests, and keep our services secure. We do not sell your personal information.`,
    ``,
    `3. Cookies`,
    `We may use cookies and similar technologies to remember your preferences and understand how our services are used. You can disable cookies in your browser settings.`,
    ``,
    `4. Third Parties`,
    `We may share information with service providers who help us operate our services, and when required by law. These providers are bound to protect your information.`,
    ``,
    `5. Data Retention`,
    `We keep personal information only as long as needed for the purposes described in this policy, or as required by law.`,
    ``,
    `6. Your Rights`,
    `Depending on your location, you may have the right to access, correct, or delete your personal information. To make a request, contact us at ${email}.`,
    ``,
    `7. Changes`,
    `We may update this policy from time to time. Continued use of ${website} after changes means you accept the updated policy.`,
    ``,
    `8. Governing Law`,
    `This policy is governed by the laws of ${country}.`,
    ``,
    `9. Contact`,
    `If you have questions about this Privacy Policy, contact us at ${email}.`,
  ].join('\n');
}

function disclaimer(v: Record<string, string>): string {
  const { company, website, email, country } = clean(v);
  return [
    `Disclaimer`,
    ``,
    `The information provided by ${company} on ${website} is for general informational purposes only. All information is provided in good faith, however we make no representation or warranty of any kind regarding its accuracy, adequacy, validity, reliability, or completeness.`,
    ``,
    `1. No Professional Advice`,
    `The content on ${website} is not a substitute for professional advice. Always seek the guidance of a qualified professional with any questions you may have.`,
    ``,
    `2. External Links`,
    `Our website may contain links to other websites. We are not responsible for the content or accuracy of any third-party sites.`,
    ``,
    `3. Limitation of Liability`,
    `Under no circumstances shall ${company} be liable for any loss or damage of any kind incurred as a result of the use of the site or reliance on any information provided.`,
    ``,
    `4. Governing Law`,
    `This disclaimer is governed by the laws of ${country}.`,
    ``,
    `5. Contact`,
    `If you have questions about this disclaimer, contact us at ${email}.`,
  ].join('\n');
}

function terms(v: Record<string, string>): string {
  const { company, website, email, country } = clean(v);
  return [
    `Terms and Conditions`,
    ``,
    `These Terms and Conditions govern your use of ${website}, operated by ${company}. By accessing or using the site, you agree to be bound by these terms.`,
    ``,
    `1. Use of the Site`,
    `You agree to use ${website} only for lawful purposes and in a way that does not infringe the rights of others or restrict their use of the site.`,
    ``,
    `2. Intellectual Property`,
    `All content on ${website}, unless stated otherwise, is the property of ${company} and is protected by applicable intellectual property laws.`,
    ``,
    `3. User Content`,
    `If you submit content to the site, you grant us a non-exclusive right to use it in connection with operating our services. You are responsible for the content you submit.`,
    ``,
    `4. Termination`,
    `We may suspend or terminate access to the site at any time, without notice, for conduct that violates these terms.`,
    ``,
    `5. Limitation of Liability`,
    `${company} is not liable for any damages arising from your use of the site, to the maximum extent permitted by law.`,
    ``,
    `6. Changes to Terms`,
    `We may revise these terms at any time. Continued use of the site means you accept the revised terms.`,
    ``,
    `7. Governing Law`,
    `These terms are governed by the laws of ${country}.`,
    ``,
    `8. Contact`,
    `For questions about these Terms and Conditions, contact us at ${email}.`,
  ].join('\n');
}

export function generateLegalDocument(
  variant: LegalVariant,
  values: Record<string, string>,
): string {
  if (variant === 'privacy-policy-generator') return privacyPolicy(values);
  if (variant === 'disclaimer-generator') return disclaimer(values);
  return terms(values);
}
